// src/clients/player.js
import '@soundworks/helpers/polyfills.js';
import { Client } from '@soundworks/core/client.js';
import { loadConfig, launcher } from '@soundworks/helpers/browser.js';
import { html, render } from 'lit';
import { initSimulation, setSimRunning } from './simulation-overlay.js'; // Helper for ?emulate=X simulation
import ClientPluginCheckin from '@soundworks/plugin-checkin/client.js';
import ClientPluginPlatformInit from '@soundworks/plugin-platform-init/client.js';
import '@soundworks/helpers/components/sw-credits.js';
import { PlayerAudioEngine } from './PlayerAudioEngine.js';
import { ENERGY_PHYSICS } from '../shared/constants.js';
import { getTestConfig } from '../shared/testConfig.js';

// ----------------------------------------------------------
// Tetris Identity Helpers (Match JUCE logic)
// ----------------------------------------------------------
const TETRIS_SHAPES = ['I', 'O', 'T', 'S', 'Z', 'J', 'L'];
const TETRIS_COLORS = [
  '#00FFFF', // I - Cyan
  '#FFFF00', // O - Yellow
  '#800080', // T - Purple
  '#00FF00', // S - Green
  '#FF0000', // Z - Red
  '#0000FF', // J - Blue
  '#FFA500', // L - Orange
];

function getTetrisForIndex(idx) {
  const shapeIdx = idx % 7;
  return {
    shape: TETRIS_SHAPES[shapeIdx],
    color: TETRIS_COLORS[shapeIdx],
    cells: [
      [[0, 0], [1, 0], [2, 0], [3, 0]], // I
      [[0, 0], [1, 0], [0, 1], [1, 1]], // O
      [[0, 0], [1, 0], [2, 0], [1, 1]], // T
      [[1, 0], [2, 0], [0, 1], [1, 1]], // S
      [[0, 0], [1, 0], [1, 1], [2, 1]], // Z
      [[0, 0], [0, 1], [1, 1], [2, 1]], // J
      [[0, 0], [1, 0], [2, 0], [0, 1]], // L
    ][shapeIdx]
  };
}

// ----------------------------------------------------------
// UI Helpers: Piece Rendering (3D Style)
// ----------------------------------------------------------
function renderMiniPiece(cells, color, size = 18, glow = true) {
  return html`
    <div style="position: relative; width: ${size * 3.5}px; height: ${size * 2.2}px; display: inline-block; vertical-align: middle; margin-left:12px; filter: drop-shadow(0 0 10px rgba(0,0,0,0.8));">
      ${cells.map(c => html`
        <div style="position: absolute; width: ${size}px; height: ${size}px; background: ${color}; 
                    left: ${c[0] * (size + 1)}px; top: ${c[1] * (size + 1)}px; 
                    border-radius: 3px;
                    box-shadow: inset 1.5px 1.5px 3px rgba(255,255,255,0.8), 
                                inset -1.5px -1.5px 3px rgba(0,0,0,0.4),
                                ${glow ? `0 0 12px ${color}` : 'none'};"></div>
      `)}
    </div>
  `;
}

// ----------------------------------------------------------
// UI Helpers: Slot Piece Rendering
// ----------------------------------------------------------
function renderTetrisSlotPiece(bandIdx, laneIdx) {
  const seed = (bandIdx * 3) + laneIdx;
  const rng = () => {
    let x = Math.sin(seed * 43758.5453) * 10000;
    return x - Math.floor(x);
  };

  // Position: Centered in lane (since we are inside the lane container)
  const posX = 50;
  const posY = 25 + (rng() * 50); // Vertical Jitter 25-75%
  const rot = Math.floor(rng() * 4) * 90;
  const opacity = 0.9 + (rng() * 0.1); // Solid pop

  const shapeIdx = Math.floor(rng() * 7);
  const color = TETRIS_COLORS[shapeIdx];
  const shapes = [
    [[0, 0], [1, 0], [2, 0], [3, 0]], // I
    [[0, 0], [1, 0], [0, 1], [1, 1]], // O
    [[0, 0], [1, 0], [2, 0], [1, 1]], // T
    [[1, 0], [2, 0], [0, 1], [1, 1]], // S
    [[0, 0], [1, 0], [1, 1], [2, 1]], // Z
    [[0, 0], [0, 1], [1, 1], [2, 1]], // J
    [[0, 0], [1, 0], [2, 0], [0, 1]], // L
  ];
  const cells = shapes[shapeIdx];
  const cellSize = 18; // Reduced from 30 to fit within lane width
  const cellGap = 2;   // Reduced gap

  // Calculate bounding box to center the shape
  let minX = Infinity, maxX = -Infinity;
  let minY = Infinity, maxY = -Infinity;
  cells.forEach(c => {
    minX = Math.min(minX, c[0]);
    maxX = Math.max(maxX, c[0]);
    minY = Math.min(minY, c[1]);
    maxY = Math.max(maxY, c[1]);
  });

  const width = (maxX - minX + 1) * (cellSize + cellGap) - cellGap;
  const height = (maxY - minY + 1) * (cellSize + cellGap) - cellGap;
  const offsetX = width / 2;
  const offsetY = height / 2;

  return html`
    <div style="position: absolute; left: ${posX}%; top: ${posY}%; transform: rotate(${rot}deg); opacity: ${opacity}; pointer-events:none; filter: drop-shadow(0 8px 15px rgba(0,0,0,0.3));">
      ${cells.map(c => html`
        <div style="position: absolute; width: ${cellSize}px; height: ${cellSize}px; background: ${color}; 
                    left: ${c[0] * (cellSize + cellGap) - offsetX}px; top: ${c[1] * (cellSize + cellGap) - offsetY}px; 
                    border-radius: 4px;
                    box-shadow: inset 2px 2px 4px rgba(255,255,255,0.7), 
                                inset -2px -2px 4px rgba(0,0,0,0.3),
                                0 0 15px ${color};"></div>
      `)}
    </div>
  `;
}

// ----------------------------------------------------------
// HSL -> RGB helper
// h, s, l in 0..1, returns [r, g, b] in 0..1
// ----------------------------------------------------------
function hslToRgb(h, s, l) {
  let r, g, b;

  if (s === 0) {
    r = g = b = l; // achromatic (grey)
  } else {
    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;

    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  return [r, g, b];
}

// ----------------------------------------------------------
// Audio System Integrated via PlayerAudioEngine.js
// ----------------------------------------------------------

// main called by launcher for each client (emulate or real)
async function main($container) {
  // Fix for emulation mode visibility: allow scrolling if we are emulating many clients
  const isEmulated = new URLSearchParams(window.location.search).has('emulate');
  if (isEmulated) {
    document.title = 'CATSINO Hub (Emulated)';
    document.documentElement.style.overflowY = 'auto';
    document.documentElement.style.height = 'auto';
    document.body.style.overflowY = 'auto';
    document.body.style.height = 'auto';
    document.body.style.background = '#000';
  } else {
    document.title = 'CATSINO Hub';
  }

  // ----------------------------------------------------------
  // Platform-Init splash screen: dark purple bg + CATSINO logo
  // Injected before launcher.register() so it's ready when the
  // plugin screen first renders.
  //
  // In emulate mode each main() call has its own $container div.
  // We scope everything to $container (position:absolute) so every
  // emulated client gets its own independent init UI.
  // ----------------------------------------------------------

  // In emulate mode: make $container a proper positioning context and
  // clip overflow so init elements stay inside their grid cell.
  if (isEmulated) {
    $container.style.position = 'relative';
    $container.style.overflow = 'hidden';
    $container.style.background = '#1a0030';
  }

  const initStyle = document.createElement('style');
  // body background only in real (non-emulate) mode — emulate sets it per-container above
  initStyle.textContent = `
    ${!isEmulated ? 'body { background: #1a0030; }' : ''}
    sw-launcher {
      background: #1a0030;
      color: rgba(220, 200, 255, 0.75);
      --title-display: none;
    }
  `;
  document.head.appendChild(initStyle);

  // CatClaw paw — centered, pink neon glow.
  // position:absolute so it sits inside $container in both real and emulate mode.
  const initClaw = document.createElement('img');
  initClaw.src = '/images/CatClaw.png';
  initClaw.style.cssText = [
    'position: absolute',
    'top: 28%',
    'left: 50%',
    'transform: translate(-50%, -50%)',
    'width: 96px',
    'height: 96px',
    'object-fit: contain',
    'filter: drop-shadow(0 0 14px rgba(255,110,199,0.95)) drop-shadow(0 0 30px rgba(255,110,199,0.5))',
    'pointer-events: none',
    'z-index: 9999',
  ].join(';');
  $container.appendChild(initClaw);

  // Game-rules card — brief two-line explainer, pointer-events off.
  const initRules = document.createElement('div');
  initRules.id = 'init-rules';
  initRules.innerHTML = `
    <div style="
      font-size:10px; font-weight:900; letter-spacing:4px;
      color:rgba(220,200,255,0.5); text-transform:uppercase;
      margin-bottom:14px; text-align:center;">
      How it works
    </div>
    <div style="
      display:flex; flex-direction:column; gap:10px;">
      <div style="display:flex; align-items:center; gap:12px;">
        <span style="
          background:rgba(0,242,254,0.15); border:1px solid rgba(0,242,254,0.4);
          color:#00f2fe; font-size:11px; font-weight:800; letter-spacing:2px;
          padding:4px 10px; border-radius:20px; white-space:nowrap;">
          SLOTS 1–25
        </span>
        <span style="color:rgba(220,200,255,0.8); font-size:13px; line-height:1.4;">
          Active — scroll to power the room
        </span>
      </div>
      <div style="display:flex; align-items:center; gap:12px;">
        <span style="
          background:rgba(255,110,199,0.12); border:1px solid rgba(255,110,199,0.35);
          color:#ff6ec7; font-size:11px; font-weight:800; letter-spacing:2px;
          padding:4px 10px; border-radius:20px; white-space:nowrap;">
          SLOTS 26–100
        </span>
        <span style="color:rgba(220,200,255,0.8); font-size:13px; line-height:1.4;">
          Queue — join when a slot opens
        </span>
      </div>
    </div>
  `;
  initRules.style.cssText = [
    'position: absolute',
    'top: 52%',
    'left: 50%',
    'transform: translate(-50%, -50%)',
    'width: 320px',
    'max-width: 88%',
    'background: rgba(255,255,255,0.04)',
    'border: 1px solid rgba(220,200,255,0.12)',
    'border-radius: 16px',
    'padding: 18px 20px',
    'font-family: system-ui, sans-serif',
    'backdrop-filter: blur(8px)',
    'pointer-events: none',
    'z-index: 9999',
  ].join(';');
  $container.appendChild(initRules);

  // Enlarge "Click to start" inside sw-plugin-platform-init's shadow root.
  // Scoped to $container so each emulated client targets its own sw-launcher.
  const launcherObserver = new MutationObserver(() => {
    const launcher = $container.querySelector('sw-launcher');
    if (!launcher?.shadowRoot) return;
    launcherObserver.disconnect();

    const platformObserver = new MutationObserver(() => {
      const platformInit = launcher.shadowRoot.querySelector('sw-plugin-platform-init');
      if (!platformInit?.shadowRoot) return;
      platformObserver.disconnect();

      const style = document.createElement('style');
      style.textContent = `
        @keyframes soft-pulse {
          0%, 100% { opacity: 0.55; }
          50%       { opacity: 0.9; }
        }
        p {
          font-size: 5rem !important;
          line-height: 1.1 !important;
          letter-spacing: 0.12em !important;
          text-transform: uppercase !important;
          font-weight: 900 !important;
          word-break: break-word !important;
          text-align: center !important;
          max-width: 92% !important;
          margin: 0 auto !important;
          animation: soft-pulse 2.4s ease-in-out infinite;
        }
      `;
      platformInit.shadowRoot.appendChild(style);
    });
    platformObserver.observe(launcher.shadowRoot, { childList: true, subtree: true });
  });
  launcherObserver.observe($container, { childList: true, subtree: true });

  const config = await loadConfig();
  const client = new Client(config, 'player');

  // Create an independent audio engine instance for this player
  const audioEngine = new PlayerAudioEngine();
  audioEngine.init(); // Creates the suspended AudioContext

  // Register plugins
  client.pluginManager.register('checkin', ClientPluginCheckin);
  client.pluginManager.register('platform-init', ClientPluginPlatformInit, {
    audioContext: audioEngine.audioContext
  });

  launcher.register(client, { initScreensContainer: $container });

  // Explicitly init (connect to server + state manager) before starting plugins.
  // This lets us attach to global state and render the init-screen button while
  // platform-init is still showing its "CLICK TO START" splash.
  await client.init();

  const global = await client.stateManager.attach('global');

  // ----------------------------------------------------------
  // Shared flash effect — full-screen overlay fades out over 600ms.
  // Color is the pressing player's tetris color (white during init).
  // Double-RAF ensures frame 0 is opaque before the transition begins.
  // flashActive dedup: if a flash is already fading, ignore the call so
  // a burst of simultaneous events never stacks multiple overlays.
  // ----------------------------------------------------------
  let flashActive = false;

  function doScreenFlash(color = '#ffffff') {
    if (flashActive) return;
    flashActive = true;
    const overlay = document.createElement('div');
    overlay.style.cssText = [
      'position:fixed', 'inset:0', 'pointer-events:none', 'z-index:99999',
      `background:${color}`, 'opacity:1', 'transition:opacity 600ms ease-out',
    ].join(';');
    document.body.appendChild(overlay);
    requestAnimationFrame(() => requestAnimationFrame(() => {
      overlay.style.opacity = '0';
      overlay.addEventListener('transitionend', () => {
        overlay.remove();
        flashActive = false;
      }, { once: true });
    }));
  }

  // Server-controlled flash freeze — read initial value and keep in sync
  let flashFrozen = global.get('flashFrozen') || false;

  // Pre-start listener: flash/vibrate reacts on clients still on the init screen
  const unsubPreStart = global.onUpdate(updates => {
    if ('trigger' in updates && updates.trigger === true) {
      doScreenFlash(updates.triggerColor || '#ffffff');
    }
    if ('vibrateAll' in updates && updates.vibrateAll === true) {
      if (navigator.vibrate) navigator.vibrate([200, 100, 200]);
    }
    if ('flashFrozen' in updates) {
      flashFrozen = updates.flashFrozen;
      // Reflect on the init-screen flash button if it still exists
      const btn = document.getElementById('init-flash-btn');
      if (btn) {
        btn.disabled = flashFrozen || btn.dataset.cooldown === 'true';
        btn.style.opacity = flashFrozen ? '0.2' : (btn.dataset.cooldown === 'true' ? '0.35' : '1');
      }
    }
  });

  // ----------------------------------------------------------
  // Init-screen controls (plain DOM, removed after client.start())
  //
  //  • CatClaw image  → broadcast vibrate to all players
  //  • ⚡ button      → broadcast screen flash to all players
  //
  // Independent 8-second cooldowns prevent accidental spam.
  // ----------------------------------------------------------

  // Make the already-visible CatClaw interactive as the vibrate trigger
  initClaw.style.pointerEvents = 'auto';
  initClaw.style.cursor = 'pointer';
  initClaw.style.transition = 'transform 0.15s ease-out, filter 0.3s ease-out, opacity 0.3s ease-out';
  initClaw.style.webkitTapHighlightColor = 'transparent';

  let clawVibrateCooldown = false;
  initClaw.addEventListener('click', () => {
    if (clawVibrateCooldown) return;
    clawVibrateCooldown = true;

    global.set({ vibrateAll: true });
    if (navigator.vibrate) navigator.vibrate([200, 100, 200]);

    // Pulse up → dim during cooldown
    initClaw.style.transform = 'translate(-50%, -50%) scale(1.35)';
    initClaw.style.filter = 'drop-shadow(0 0 32px rgba(255,110,199,1)) drop-shadow(0 0 60px rgba(255,110,199,0.7))';
    setTimeout(() => {
      initClaw.style.transform = 'translate(-50%, -50%) scale(1)';
      initClaw.style.filter = 'drop-shadow(0 0 6px rgba(255,110,199,0.4))';
      initClaw.style.opacity = '0.4';
    }, 200);

    setTimeout(() => {
      clawVibrateCooldown = false;
      initClaw.style.filter = 'drop-shadow(0 0 14px rgba(255,110,199,0.95)) drop-shadow(0 0 30px rgba(255,110,199,0.5))';
      initClaw.style.opacity = '1';
    }, 8000);
  });

  // Small hint label beneath the claw
  const initClawLabel = document.createElement('div');
  initClawLabel.textContent = 'tap · vibrate all';
  initClawLabel.style.cssText = [
    'position:absolute', 'top:35%', 'left:50%', 'transform:translateX(-50%)',
    'font-size:10px', 'font-weight:700', 'letter-spacing:3px',
    'text-transform:uppercase', 'color:rgba(255,110,199,0.55)',
    'font-family:system-ui,sans-serif',
    'pointer-events:none', 'z-index:9999', 'white-space:nowrap',
  ].join(';');
  $container.appendChild(initClawLabel);

  // ⚡ Flash button — positioned between the rules card and "CLICK TO START"
  const initFlashBtn = document.createElement('button');
  initFlashBtn.innerHTML = '⚡&nbsp;&nbsp;FLASH ALL';
  initFlashBtn.style.cssText = [
    'position:absolute', 'top:74%', 'left:50%', 'transform:translateX(-50%)',
    'background:rgba(255,220,80,0.1)', 'border:1.5px solid rgba(255,220,80,0.5)',
    'border-radius:28px', 'padding:12px 28px',
    'font-size:13px', 'font-weight:800', 'letter-spacing:3px',
    'color:#ffe066', 'cursor:pointer', 'pointer-events:auto',
    'z-index:9999', 'font-family:system-ui,sans-serif',
    'text-transform:uppercase', 'backdrop-filter:blur(10px)',
    'transition:opacity 0.3s', '-webkit-tap-highlight-color:transparent',
  ].join(';');

  let initFlashCooldown = false;
  initFlashBtn.addEventListener('click', () => {
    if (initFlashCooldown || flashFrozen) return;
    initFlashCooldown = true;
    initFlashBtn.dataset.cooldown = 'true';

    global.set({ trigger: true, triggerColor: '#ffffff' }); // white: no checkin yet

    initFlashBtn.style.opacity = '0.35';
    initFlashBtn.style.cursor = 'default';
    initFlashBtn.disabled = true;

    let secs = 8;
    initFlashBtn.textContent = `${secs}s`;
    const tick = setInterval(() => {
      secs--;
      if (secs <= 0) {
        clearInterval(tick);
        initFlashCooldown = false;
        initFlashBtn.dataset.cooldown = 'false';
        if (!flashFrozen) {
          initFlashBtn.disabled = false;
          initFlashBtn.style.opacity = '1';
          initFlashBtn.style.cursor = 'pointer';
        }
        initFlashBtn.innerHTML = '⚡&nbsp;&nbsp;FLASH ALL';
      } else {
        initFlashBtn.textContent = `${secs}s`;
      }
    }, 1000);
  });

  $container.appendChild(initFlashBtn);

  // Platform-init plugin shows "CLICK TO START" and waits for user gesture
  await client.start();

  // Tear down all init-screen DOM once the player enters the game
  unsubPreStart();
  initClaw.remove();
  initClawLabel.remove();
  initRules.remove();
  initFlashBtn.remove();

  // Get stable slot from checkin plugin (0..N)
  const checkin = await client.pluginManager.get('checkin');
  const playerIndex = checkin.getIndex();
  const tetris = getTetrisForIndex(playerIndex);

  // global is already attached above (before client.start())
  console.log('[client] global state initial:', global.getValues());

  let muted = global.get('muteAll') || false;
  let scene = global.get('scene') || 0;

  function applyMuteVisual() {
    $container.style.opacity = muted ? 0.4 : 1.0;
  }
  applyMuteVisual();

  const isEmulateMode = isEmulated;

  // Play join tone if we are in the active player pool
  if (playerIndex !== null && playerIndex < 25) {
    setTimeout(() => audioEngine.playJoinTone(playerIndex), 100);
  }

  // Local audio mute state (independent of global mute)
  let audioMuted = false;

  // Independent flash / vibrate cooldowns for the in-game buttons
  let flashCooldown = false;
  let flashSecs = 0;
  let vibrateCooldown = false;
  let vibrateSecs = 0;
  // Mirror of global.flashFrozen — already tracked above, shared via closure
  // (flashFrozen variable is declared before client.start())

  function startCooldown(setActive, setSecs, ms = 8000) {
    setActive(true);
    setSecs(ms / 1000);
    renderApp();
    const tick = setInterval(() => {
      setSecs(prev => {
        const next = prev - 1;
        if (next <= 0) {
          clearInterval(tick);
          setActive(false);
          setSecs(0);
        }
        renderApp();
        return next;
      });
    }, 1000);
  }

  function triggerFlash() {
    if (flashCooldown || flashFrozen) return;
    global.set({ trigger: true, triggerColor: tetris.color });
    doScreenFlash(tetris.color);
    startCooldown(
      v => { flashCooldown = v; },
      fn => { flashSecs = typeof fn === 'function' ? fn(flashSecs) : fn; }
    );
  }

  function triggerVibrate() {
    if (vibrateCooldown) return;
    global.set({ vibrateAll: true });
    if (navigator.vibrate) navigator.vibrate([200, 100, 200]);
    startCooldown(
      v => { vibrateCooldown = v; },
      fn => { vibrateSecs = typeof fn === 'function' ? fn(vibrateSecs) : fn; }
    );
  }

  function toggleAudioMute() {
    audioMuted = !audioMuted;

    // Trigger re-render to update icon/style reactively
    renderApp();

    // Immediately silence if muting (use small non-zero floor for ramps)
    if (audioMuted && audioEngine.isInitialized) {
      const now = audioEngine.audioContext.currentTime;
      audioEngine.gainNode.gain.cancelScheduledValues(now);
      audioEngine.gainNode.gain.setValueAtTime(0.0001, now);
    }
  }

  global.onUpdate(updates => {
    try {
      // console.log('[global.onUpdate] Received updates:', Object.keys(updates));

      if ('trigger' in updates && updates.trigger === true) {
        doScreenFlash(updates.triggerColor || '#ffffff');
      }

      if ('flashFrozen' in updates) {
        flashFrozen = updates.flashFrozen;
        renderApp(); // refresh button disabled state
      }

      // muteAll
      if ('muteAll' in updates) {
        muted = updates.muteAll;
        applyMuteVisual();
      }

      // scene
      if ('scene' in updates) {
        scene = updates.scene;
        console.log('[client] scene changed:', scene);
      }

      // reload command
      if ('reloadAll' in updates && updates.reloadAll === true) {
        console.log('[client] reload command received');
        setTimeout(() => {
          window.location.reload();
        }, 100);
      }

      // vibrate command (haptic feedback)
      if ('vibrateAll' in updates && updates.vibrateAll === true) {
        console.log('[client] vibrate command received');
        if (navigator.vibrate) {
          navigator.vibrate([200, 100, 200]);
        }
      }

      // globalEmulate (Remote Simulation)
      if ('globalEmulate' in updates) {
        if (updates.globalEmulate === true) {
          console.log('[client] Global Emulation START');
          if (navigator.vibrate) navigator.vibrate(200);
        } else {
          console.log('[client] Global Emulation STOP');
        }
      }

      // globalEnergy (Community Progress Bar) - MUST BE IN SAME LISTENER!
      if ('globalEnergy' in updates) {
        // console.log('[global.onUpdate] globalEnergy changed to:', updates.globalEnergy);
        updateCommunityBarVisual(updates.globalEnergy);
      }
    } catch (err) {
      console.error('[global.onUpdate] Error processing update:', err);
    }
  });

  // ----------------------------------------------------------
  // 2. Create per-client state (color, x, y, scroll, + baseXY, energy, speed, HSL, RGB)
  // ----------------------------------------------------------
  const baseX = Math.random();
  const baseY = Math.random();

  const playerState = await client.stateManager.create('player', {
    id: client.id,
    playerIndex: playerIndex, // Stable 0-24 index
    tetrisShape: tetris.shape,
    tetrisColor: tetris.color,
    // base position in the global field (for controller & Max)
    baseX,
    baseY,
    // actual position starts at base
    x: baseX,
    y: baseY,
    scroll: 0,
    // new fields:
    energy: 0,
    speed: 0,
    color: '#000000',
    hue: 0,
    sat: 0,
    light: 0,
    // RGB (0..1)
    r: 0,
    g: 0,
    b: 0,
  });

  // Set initial stereo pan based on base position (PHASE 1: Spatialization)
  audioEngine.updatePan(baseX);

  playerState.onUpdate(updates => {
    if ('color' in updates) {
      $container.style.backgroundColor = updates.color;
    }
    // PHASE 1: Update stereo panning when horizontal position changes
    if ('x' in updates) {
      audioEngine.updatePan(updates.x);
    }
  });

  // ----------------------------------------------------------
  // 3. Render UI with a scrollable (virtual) color column (Carousel Style)
  // ----------------------------------------------------------
  const numBands = 20;
  // Carousel: 1 full + subtle neighbor crop = 1.25 ratio (Social Feed Style)
  let bandHeight = window.innerHeight / 1.25;
  let cycleHeight = numBands * bandHeight;

  // Handle resize to keep carousel proportions
  window.addEventListener('resize', () => {
    const oldBandHeight = bandHeight;
    bandHeight = window.innerHeight / 1.25;
    cycleHeight = numBands * bandHeight;

    // Only re-render if size actually changed
    if (Math.abs(oldBandHeight - bandHeight) > 1) {
      renderApp();
      // Re-initialize community bar after Lit completes render
      requestAnimationFrame(() => {
        const currentEnergy = global.get('globalEnergy') || 0;
        updateCommunityBarVisual(currentEnergy);
      });
    }
  });

  // Helper: Generate a random Tetris-style piece for the background (3D Style)

  // Direct DOM update for performance (bypasses Lit render)
  function updateCommunityBarVisual(energy) {
    const fill = $container.querySelector('#community-fill-bar');
    if (!fill) {
      // console.warn('[updateCommunityBarVisual] Bar element not found, globalEnergy:', energy);
      return;
    }

    let color = '#00f2fe'; // Cool Cyan (Default)
    if (energy > 0.8) color = '#ff4b2b';      // Hot Red/Orange
    else if (energy > 0.4) color = '#7cf29c'; // Vibrant Green

    fill.style.width = `${Math.min(100, energy * 100)}%`;
    fill.style.backgroundColor = color;
    fill.style.boxShadow = `0 0 20px ${color}`;

    if (energy > 0.8) fill.classList.add('pulsing');
    else fill.classList.remove('pulsing');
  }

  // Manual DOM updates for performance (no full re-render)
  function updateHintVisibility(visible) {
    const hintOverlay = $container.querySelector('.hint-overlay');
    if (hintOverlay) {
      if (visible) {
        hintOverlay.classList.add('visible');
      } else {
        hintOverlay.classList.remove('visible');
      }
    }
  }

  function updateCountdown(seconds) {
    const countdownEl = $container.querySelector('.countdown-timer');
    if (countdownEl) {
      if (seconds > 0) {
        countdownEl.textContent = `Expiring in ${seconds}s`;
        countdownEl.style.display = 'block';
      } else {
        countdownEl.textContent = '';
        countdownEl.style.display = 'none';
      }
    }
  }

  function updateTimeoutScreen(show) {
    let timeoutOverlay = $container.querySelector('.timeout-overlay');
    if (show && !timeoutOverlay) {
      // Create timeout overlay dynamically
      timeoutOverlay = document.createElement('div');
      timeoutOverlay.className = 'fullscreen-overlay timeout-overlay';
      timeoutOverlay.innerHTML = `
        <h2 style="color: #ff4444">SESSION EXPIRED</h2>
        <p>You were disconnected due to inactivity.</p>
        <button class="action-btn" onclick="window.location.reload()">REJOIN</button>
      `;
      $container.appendChild(timeoutOverlay);
    } else if (!show && timeoutOverlay) {
      timeoutOverlay.remove();
    }
  }

  function renderApp() {
    const bands = Array.from({ length: numBands }, (_, i) => {
      // Categorical color blocks for integrated slot aesthetic
      const step = Math.floor(i / (numBands / 7));
      const baseHue = step * (360 / 7);
      const jitter = (i % 2) * 20;
      const color = `hsl(${baseHue + jitter}, 75%, 60%)`;

      return html`
        <div class="carousel-card-container" style="height: ${bandHeight}px;">
          <div class="carousel-card" style="background: ${color};">
            <div class="slot-lane">${renderTetrisSlotPiece(i, 0)}</div>
            <div class="slot-lane">${renderTetrisSlotPiece(i, 1)}</div>
            <div class="slot-lane">${renderTetrisSlotPiece(i, 2)}</div>
          </div>
        </div>
      `;
    });

    render(html`
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;800&display=swap');

        * {
          box-sizing: border-box;
          margin: 0;
          padding: 0;
        }

        body, html {
          overflow: hidden;
          background: #000; /* Deep Void */
        }

        #container {
          position: relative;
          width: 100%;
          height: 100%;
          overflow: hidden;
          background: 
            linear-gradient(rgba(0,0,0,0.9), rgba(0,0,0,0.9)),
            linear-gradient(0deg, rgba(0, 255, 255, 0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0, 255, 255, 0.1) 1px, transparent 1px);
          background-size: 100% 100%, 40px 40px, 40px 40px;
        }

        /* Bands Container */
        .bands-container {
          position: absolute;
          width: 100%;
          z-index: 1;
          will-change: transform;
        }

        .carousel-card-container {
          padding: 15px 12px;
          box-sizing: border-box;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        /* Color Block Cards (Slot Machine Style) */
        .carousel-card {
          width: 100%;
          height: 100%;
          border-radius: 20px;
          position: relative;
          overflow: hidden;
          box-shadow: 0 8px 30px rgba(0,0,0,0.3);
          display: flex;
          justify-content: space-around;
          align-items: center;
          border: 2px solid rgba(255,255,255,0.3);
        }

        .slot-lane {
          flex: 1;
          height: 100%;
          position: relative;
          border-left: 1px solid rgba(255,255,255,0.2);
          border-right: 1px solid rgba(0,0,0,0.1);
          background: linear-gradient(180deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.05) 50%, rgba(255,255,255,0.15) 100%);
        }

        .slot-lane:first-child { border-left: none; }
        .slot-lane:last-child { border-right: none; }

        /* Community Progress (TOP) - Tech HUD Style */
        .community-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          padding: 20px 20px;
          z-index: 100;
          background: rgba(0, 0, 0, 0.9);
          backdrop-filter: blur(10px);
          display: flex;
          flex-direction: column;
          align-items: center;
          border-bottom: 1px solid rgba(0, 255, 255, 0.2);
          box-shadow: 0 5px 20px rgba(0,0,0,0.5);
        }

        .community-label {
          font-size: 11px;
          font-weight: 900;
          letter-spacing: 4px;
          color: #00f2fe; /* Cyan Text */
          margin-bottom: 10px;
          text-transform: uppercase;
        }

        .community-bar {
          width: 90%;
          height: 12px;
          background: #111;
          border-radius: 6px;
          overflow: hidden;
          border: 1px solid #333;
          position: relative;
          box-shadow: inset 0 0 10px rgba(0,0,0,0.8);
        }

        .community-fill {
          height: 100%;
          width: 0%;
          transition: width 0.4s cubic-bezier(0.23, 1, 0.32, 1), background 0.5s ease;
          box-shadow: inset 0 0 10px rgba(255,255,255,0.5);
          position: relative;
          overflow: hidden;
        }

        /* Shimmer Motivation Effect */
        .community-fill::after {
          content: "";
          position: absolute;
          top: 0;
          left: -100%;
          width: 50%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent);
          animation: barShimmer 2.5s infinite linear;
        }

        @keyframes barShimmer {
          0% { left: -100%; }
          100% { left: 200%; }
        }

        .community-fill.pulsing {
          animation: communityPulse 0.8s infinite alternate;
        }

        @keyframes communityPulse {
          from { filter: brightness(1) drop-shadow(0 0 5px rgba(0,0,0,0.2)); }
          to { filter: brightness(1.3) drop-shadow(0 0 20px currentColor); }
        }

        /* Floating header overlay - Social High Contrast */
        .header-overlay {
          position: absolute;
          top: 90px; 
          left: 20px;
          right: 20px;
          padding: 15px;
          z-index: 10;
          pointer-events: none;
          background: rgba(0,0,0,0.85); /* Deep dark for visibility on white */
          backdrop-filter: blur(10px);
          border-radius: 20px;
          box-shadow: 0 10px 30px rgba(0,0,0,0.3);
          border: 1px solid rgba(255,255,255,0.1);
        }

        .header-overlay .player-id {
          margin: 0;
          font-size: 18px;
          letter-spacing: 2px;
          text-transform: uppercase;
          color: #FFFFFF;
          font-weight: 800;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        /* Hint Overlay (The Organic Swipe) */
        .hint-overlay {
          position: absolute;
          inset: 0;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          z-index: 50;
          background: rgba(0,0,0,0.4);
          backdrop-filter: blur(4px);
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.5s ease;
        }

        .hint-overlay.visible {
          opacity: 1;
        }

        .hint-icon {
          font-size: 80px;
          animation: organicSwipe 2s cubic-bezier(0.445, 0.05, 0.55, 0.95) infinite;
          filter: drop-shadow(0 0 20px ${tetris.color});
        }

        .hint-text {
          margin-top: 10px;
          font-size: 12px;
          letter-spacing: 4px;
          text-transform: uppercase;
          font-weight: 700;
          color: white;
        }

        @keyframes organicSwipe {
          0% { transform: translateY(100px) scale(0.8); opacity: 0; }
          20% { transform: translateY(80px) scale(1.1); opacity: 1; }
          80% { transform: translateY(-100px) scale(0.9); opacity: 1; }
          100% { transform: translateY(-120px) scale(0.8); opacity: 0; }
        }

        /* Countdown display */
        .countdown-timer {
          margin-top: 20px;
          color: #ff4444;
          font-weight: 800;
          font-size: 18px;
          letter-spacing: 1px;
        }

        /* Unified Overlay Screen */
        .fullscreen-overlay {
          position: absolute;
          inset: 0;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          background: rgba(0, 0, 0, 0.85);
          backdrop-filter: blur(15px);
          z-index: 200;
          text-align: center;
          padding: 40px;
          transform: translateZ(0); /* Hardware acceleration */
        }

        .fullscreen-overlay h2 {
          font-size: 28px;
          letter-spacing: 4px;
          margin-bottom: 24px;
          text-transform: uppercase;
          font-weight: 800;
        }

        .fullscreen-overlay p {
          color: rgba(255, 255, 255, 0.6);
          font-size: 16px;
          line-height: 1.6;
          max-width: 300px;
        }

        .fullscreen-overlay .action-btn {
          margin-top: 30px;
          padding: 16px 32px;
          background: #fff;
          color: #000;
          border: none;
          border-radius: 30px;
          font-weight: 800;
          letter-spacing: 2px;
          text-transform: uppercase;
          cursor: pointer;
          transition: transform 0.2s ease;
        }

        .fullscreen-overlay .action-btn:active {
          transform: scale(0.95);
        }

        .spinner {
          width: 60px;
          height: 60px;
          border: 5px solid rgba(255,255,255,0.1);
          border-top-color: #fff;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 30px;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        /* Full-screen scroll area */
        .scroll-area-fullscreen {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          overflow: hidden;
          touch-action: none; /* Crucial for custom touch handling */
        }

        .bands-container {
          will-change: transform;
          transform: translate3d(0, 0, 0); /* Hardware acceleration */
        }

        .color-band {
          width: 100%;
        }

        /* Radial rings */
        .energy-rings {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          pointer-events: none;
          z-index: 5;
          transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .energy-ring {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          border-radius: 50%;
          border: 5px solid rgba(127, 242, 156, 0.3);
          opacity: 0;
          transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 0 20px rgba(127, 242, 156, 0.4),
                      inset 0 0 20px rgba(127, 242, 156, 0.2);
        }

        .energy-ring.active {
          opacity: 1;
          animation: ringPulseRotate 4s ease-in-out infinite;
        }

        @keyframes ringPulseRotate {
          0% { 
            transform: translate(-50%, -50%) scale(1) rotate(0deg);
            border-color: rgba(127, 242, 156, 0.4);
            box-shadow: 0 0 20px rgba(127, 242, 156, 0.5),
                        inset 0 0 20px rgba(127, 242, 156, 0.3);
          }
          25% {
            border-color: rgba(242, 212, 124, 0.5);
            box-shadow: 0 0 30px rgba(242, 212, 124, 0.6),
                        inset 0 0 30px rgba(242, 212, 124, 0.4);
          }
          50% { 
            transform: translate(-50%, -50%) scale(1.2) rotate(180deg);
            border-color: rgba(242, 106, 106, 0.6);
            box-shadow: 0 0 40px rgba(242, 106, 106, 0.7),
                        inset 0 0 40px rgba(242, 106, 106, 0.5);
          }
          75% {
            border-color: rgba(242, 212, 124, 0.5);
            box-shadow: 0 0 30px rgba(242, 212, 124, 0.6),
                        inset 0 0 30px rgba(242, 212, 124, 0.4);
          }
          100% { 
            transform: translate(-50%, -50%) scale(1) rotate(360deg);
            border-color: rgba(127, 242, 156, 0.4);
            box-shadow: 0 0 20px rgba(127, 242, 156, 0.5),
                        inset 0 0 20px rgba(127, 242, 156, 0.3);
          }
        }

        /* Hearts container */
        .hearts {
          position: absolute;
          inset: 0;
          pointer-events: none;
          z-index: 8;
          overflow: hidden;
        }

        .heart {
          position: absolute;
          font-size: 48px;
          animation: heartFloatBounce 2.8s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
          opacity: 0;
          filter: drop-shadow(0 0 8px rgba(255, 100, 150, 0.8));
        }

        @keyframes heartFloatBounce {
          0% {
            opacity: 0;
            transform: translateY(0) scale(0.2) rotate(-15deg);
          }
          10% {
            opacity: 1;
            transform: translateY(-10px) scale(1.4) rotate(5deg);
          }
          20% {
            transform: translateY(-15px) scale(1.1) rotate(-5deg);
          }
          100% {
            opacity: 0;
            transform: translateY(-180px) scale(1.3) rotate(25deg);
            filter: drop-shadow(0 0 15px rgba(255, 100, 150, 1));
          }
        }

        /* Floating energy indicator */
        .energy-indicator {
          position: absolute;
          bottom: 30px;
          left: 50%;
          transform: translateX(-50%);
          text-align: center;
          z-index: 10;
          background: rgba(0, 0, 0, 0.7);
          padding: 12px 20px;
          border-radius: 24px;
          border: 1px solid rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
          min-width: 120px;
        }

        .energy-label {
          font-size: 10px;
          font-weight: 600;
          color: rgba(255, 255, 255, 0.6);
          letter-spacing: 1px;
          margin-bottom: 6px;
        }

        .energy-meter {
          width: 100px;
          height: 5px;
          background: rgba(255, 255, 255, 0.15);
          border-radius: 3px;
          overflow: hidden;
          margin: 0 auto 8px;
        }

        .energy-fill {
          height: 100%;
          background: linear-gradient(90deg, #7cf29c, #f2d47c, #f26a6a);
          transition: width 0.5s ease-out;
          border-radius: 3px;
        }

        .encouragement {
          font-size: 13px;
          color: white;
          font-weight: 500;
          transition: all 0.4s ease;
        }

        .encouragement.level-up {
          animation: levelUpPulse 0.6s ease-out;
        }

        @keyframes levelUpPulse {
          0%, 100% { 
            transform: scale(1); 
          }
          50% { 
            transform: scale(1.15); 
            color: #7cf29c;
          }
        }
      </style>

      <div class="fullscreen-layout">
        <div class="tetris-bg">${tetris.shape}</div>

        <!-- Community Progress (Global Tank) -->
        <div class="community-overlay">
          <div class="community-label">COMMUNITY PROGRESS</div>
          <div class="community-bar">
            <!-- Decoupled: We do NOT set style here, we let updateCommunityBarVisual handle it imperatively -->
            <div id="community-fill-bar" class="community-fill"></div>
          </div>
        </div>

        <!-- Floating header -->
        <div class="header-overlay">
          <p class="player-id">
            Player #${playerIndex !== null ? playerIndex + 1 : '?'} 
            ${renderMiniPiece(tetris.cells, tetris.color)}
          </p>
          <!-- Flash ⚡ + Vibrate 🐾 : top-left, all players including emulate mode -->
          <div style="position:absolute;top:8px;left:10px;display:flex;gap:6px;pointer-events:auto;z-index:1000;">
            <!-- ⚡ Flash — disabled during server freeze OR per-player cooldown -->
            <button @click=${triggerFlash} ?disabled=${flashCooldown || flashFrozen}
                    style="background:${(flashCooldown || flashFrozen) ? 'rgba(0,0,0,0.35)' : 'rgba(255,220,80,0.12)'};
                           border:1px solid ${(flashCooldown || flashFrozen) ? 'rgba(255,255,255,0.1)' : 'rgba(255,220,80,0.55)'};
                           border-radius:18px;padding:0 10px;height:36px;
                           font-size:${flashCooldown ? '11px' : '16px'};
                           color:${(flashCooldown || flashFrozen) ? 'rgba(255,255,255,0.3)' : '#ffe066'};
                           cursor:${(flashCooldown || flashFrozen) ? 'default' : 'pointer'};
                           backdrop-filter:blur(10px);transition:all 0.25s;
                           min-width:36px;display:flex;align-items:center;justify-content:center;">
              ${flashCooldown
                ? html`<span style="font-weight:700;letter-spacing:1px;">${flashSecs}s</span>`
                : flashFrozen
                  ? html`<span style="font-size:11px;font-weight:700;letter-spacing:1px;">🔒</span>`
                  : '⚡'}
              <style>button:not([disabled]):active{transform:scale(0.85);}</style>
            </button>
            <!-- 🐾 Vibrate -->
            <button @click=${triggerVibrate} ?disabled=${vibrateCooldown}
                    style="background:${vibrateCooldown ? 'rgba(0,0,0,0.35)' : 'rgba(255,110,199,0.12)'};
                           border:1px solid ${vibrateCooldown ? 'rgba(255,255,255,0.1)' : 'rgba(255,110,199,0.5)'};
                           border-radius:18px;padding:0 10px;height:36px;
                           font-size:${vibrateCooldown ? '11px' : '18px'};
                           color:${vibrateCooldown ? 'rgba(255,255,255,0.3)' : '#ff6ec7'};
                           cursor:${vibrateCooldown ? 'default' : 'pointer'};
                           backdrop-filter:blur(10px);transition:all 0.25s;
                           min-width:36px;display:flex;align-items:center;justify-content:center;">
              ${vibrateCooldown ? html`<span style="font-weight:700;letter-spacing:1px;">${vibrateSecs}s</span>` : '🐾'}
            </button>
          </div>

          <!-- Mute: top-right, real devices only (no audio in emulate mode) -->
          ${!isEmulateMode ? html`
            <button class="audio-mute-btn" @click=${toggleAudioMute}
                    style="position:absolute;top:8px;right:12px;
                           background:rgba(0,0,0,0.6);border:1px solid rgba(255,255,255,0.2);
                           border-radius:50%;width:36px;height:36px;font-size:18px;
                           cursor:pointer;transition:all 0.2s;backdrop-filter:blur(10px);
                           opacity:${audioMuted ? '0.5' : '1.0'};
                           pointer-events:auto;z-index:1000;">
              ${audioMuted ? '🔇' : '🔊'}
              <style>.audio-mute-btn:active{transform:scale(0.85);background:rgba(255,255,255,0.2);}</style>
            </button>
          ` : ''}
        </div>

        <!-- Waiting Room (Session Full: indices 25+) -->
        ${(playerIndex === null || playerIndex >= 25) ? html`
          <div class="fullscreen-overlay">
            <div class="spinner" style="border-top-color: ${tetris.color}"></div>
            <h2 style="color: ${tetris.color}">SESSION FULL</h2>
            <p>All active slots are currently occupied.<br>You are in the queue and will join automatically when a slot opens.</p>
          </div>
        ` : ''}

        <!-- Hint Overlay -->
        <div class="hint-overlay ${hintVisible ? 'visible' : ''}">
          <div class="hint-icon">👆</div>
          <p class="hint-text">Swipe Up to Play</p>
          <div class="countdown-timer" style="display: none;"></div>
        </div>

        <!-- Timeout screen handled by updateTimeoutScreen() function -->

        <!-- Full-screen scroll area -->
        <div class="scroll-area-fullscreen scroll-area">
          <div class="bands-container">
            ${bands}
            ${bands}
          </div>
          
          <!-- Radial rings for parallax -->
          <div class="energy-rings" id="energy-rings">
            <div class="energy-ring" id="ring-1" style="width: 300px; height: 300px;"></div>
            <div class="energy-ring" id="ring-2" style="width: 500px; height: 500px;"></div>
            <div class="energy-ring" id="ring-3" style="width: 700px; height: 700px;"></div>
          </div>
          
          <!-- Hearts/Likes -->
          <div class="hearts" id="hearts"></div>
        </div>

        <!-- Energy indicator -->
        <div class="energy-indicator">
          <div class="energy-label">ENERGY</div>
          <div class="energy-meter">
            <div class="energy-fill" id="energy-fill" style="width: 0%;"></div>
          </div>
          <div class="encouragement" id="encouragement">Scroll to play!</div>
        </div>
      </div>
    `, $container);

    // POST-RENDER SYNC (Crucial)
    // We must manually apply the energy state after every renderApp(),
    // otherwise the static empty bar from template will replace our dynamic width.
    requestAnimationFrame(() => {
      const currentEnergy = global.get('globalEnergy') || 0;
      updateCommunityBarVisual(currentEnergy);
    });
  }

  // ----------------------------------------------------------
  // Idle/Hint State Machine (Test Mode Configurable)
  // ----------------------------------------------------------
  let idleStart = Date.now();
  let hintVisible = false;
  let countdown = 0;
  let isTimedOut = false;

  // Timeout delays from test mode config
  const IDLE_HINT_DELAY = getTestConfig('IDLE_HINT_DELAY');
  const IDLE_COUNTDOWN_DELAY = getTestConfig('IDLE_COUNTDOWN_DELAY');
  const IDLE_TIMEOUT_TOTAL = getTestConfig('IDLE_TIMEOUT_TOTAL');

  // Initial render (ONCE)
  renderApp();

  // Initialize community bar with current state
  setTimeout(() => {
    const currentEnergy = global.get('globalEnergy') || 0;
    // console.log('[INIT] Initializing community bar with energy:', currentEnergy);
    updateCommunityBarVisual(currentEnergy);
  }, 100);

  // Periodic check (1s)
  setInterval(() => {
    if (isTimedOut || playerIndex === null) return;

    const now = Date.now();
    const idleTime = now - idleStart;

    // 1. Check for Hint
    if (idleTime > IDLE_HINT_DELAY) {
      // Update UI manually
      if (!hintVisible) {
        updateHintVisibility(true);
        hintVisible = true;
      }

      // 2. Start Countdown
      const remaining = Math.ceil((IDLE_TIMEOUT_TOTAL - idleTime) / 1000);
      const countdownThreshold = (IDLE_TIMEOUT_TOTAL - IDLE_COUNTDOWN_DELAY) / 1000;
      if (remaining <= countdownThreshold && remaining > 0) {
        countdown = remaining;
        updateCountdown(remaining);
      } else {
        countdown = 0;
        updateCountdown(0);
      }
    }

    // 3. Check for Final Timeout
    if (idleTime > IDLE_TIMEOUT_TOTAL) {
      isTimedOut = true;
      console.log('[client] Session Timed Out');
      updateTimeoutScreen(true);
    }

    // 4. FAILSAFE: Force-sync community bar (Every 1s)
    // Ensures eventual consistency even if a packet was missed
    if (global) {
      const currentEnergy = global.get('globalEnergy') || 0;
      updateCommunityBarVisual(currentEnergy);
    }
  }, 1000);

  // 5. HEARTBEAT: Keep server updated even when idle (Every 2s)
  setInterval(() => {
    if (playerIndex !== null && playerIndex < 25) {
      playerState.set({ lastPing: Date.now() });
    }
  }, 2000);

  function resetIdleClock() {
    idleStart = Date.now();
    if (hintVisible) {
      hintVisible = false;
      updateHintVisibility(false);
    }
    if (countdown > 0) {
      countdown = 0;
      updateCountdown(0);
    }
  }

  // ----------------------------------------------------------
  // 4. Infinite "virtual scroll" – optimized for mobile
  // ----------------------------------------------------------
  // Seed initial Scroll to match Tetris Identity Color
  const TETRIS_HUES = [180, 60, 300, 120, 0, 240, 30]; // I, O, T, S, Z, J, L
  const startHue = TETRIS_HUES[playerIndex % 7] || 0;
  let virtualScroll = (startHue / 360) * cycleHeight;
  audioEngine.syncScrollBaseline(virtualScroll); // Sync audio trigger to initial scroll

  // Query DOM elements AFTER renderApp() creates them
  const scrollArea = $container.querySelector('.scroll-area');
  const bandsContainer = $container.querySelector('.bands-container');

  if (!scrollArea || !bandsContainer) {
    console.error('[client] Critical DOM elements not found! ScrollArea:', scrollArea, 'BandsContainer:', bandsContainer);
    return; // Cannot proceed without these elements
  }

  // Force initial state update to sync color to server/UI
  setTimeout(() => {
    if (playerIndex !== null) applyVirtualScroll();
  }, 0);

  // --- SIMULATION HOOK ---
  // Expose API for external simulation script (Auto-Scroll)
  $container.simulateScroll = (deltaY) => {
    if (isTimedOut || playerIndex === null) return;

    // REVERSE: If user wants social media style, 
    // we want swipe up to move forward.
    virtualScroll += deltaY;

    // Virtual Loop Reset
    if (virtualScroll > cycleHeight * 100) {
      virtualScroll -= cycleHeight * 100;
    }

    resetIdleClock();
    scheduleUpdate();
  };

  // Register with Overlay
  initSimulation($container);

  // Listen for Global Emulate state from controller
  global.onUpdate(updates => {
    if ('globalEmulate' in updates) {
      setSimRunning(updates.globalEmulate);
    }
  });

  let lastTouchY = null;

  // Momentum Physics
  let velocity = 0;
  let isDragging = false;
  let momentumID = null;
  const FRICTION = 0.95;

  let needsUpdate = false;     // flag to tell rAF we have new data
  let lastSentTime = 0;        // for throttling playerState.set
  const stateSendInterval = 50; // ms between state updates (~20 Hz)

  // for speed calculation
  let lastVirtualScroll = 0;
  let lastSpeedTime = performance.now();

  // Contribution meter (replaces scroll position for energy feedback)
  let playerContribution = 0;

  function scheduleUpdate() {
    if (!needsUpdate) {
      needsUpdate = true;
      requestAnimationFrame(applyVirtualScroll);
    }
  }

  // Hearts generation - ULTRA FREQUENT FOR JACKPOT FEEL
  let lastHeartTime = 0;
  let lastEncouragementTime = 0; // Fixed: Restored missing variable
  const heartInterval = 150; // ms between hearts (super fast)
  const heartEmojis = ['❤️', '💕', '💖', '💗', '💙', '💜', '🧡', '💚', '💛', '✨', '💎'];
  const HEART_POOL_SIZE = 20;
  const heartPool = [];

  function createHeart() {
    const heartsContainer = $container.querySelector('#hearts');
    if (!heartsContainer) return;

    // PERFORMANCE: Use simple element pooling
    let heart;
    if (heartPool.length > 0) {
      heart = heartPool.pop();
      heart.style.display = 'block';
    } else {
      heart = document.createElement('div');
      heart.className = 'heart';
      heartsContainer.appendChild(heart);
    }

    heart.textContent = heartEmojis[Math.floor(Math.random() * heartEmojis.length)];
    heart.style.left = `${Math.random() * 80 + 10}%`;
    heart.style.top = `${Math.random() * 70 + 15}%`;

    // PERFORMANCE FIX: Removed forced reflow (offsetHeight) - causes layout thrashing
    // The animation will restart naturally when the element is re-shown

    setTimeout(() => {
      heart.style.display = 'none';
      heartPool.push(heart);
    }, 2800);
  }

  function updateEnergyUI(energy, speed) {
    const energyFill = $container.querySelector('#energy-fill');
    const ring1 = $container.querySelector('#ring-1');
    const ring2 = $container.querySelector('#ring-2');
    const ring3 = $container.querySelector('#ring-3');

    if (energyFill) {
      energyFill.style.width = `${energy * 100}%`;
    }

    // Dynamic feedback based on speed (with cooldown to prevent flicker)
    // Thresholds recalibrated for 0.12 speed factor (easier to reach max)
    const nowEncourage = performance.now();
    if (nowEncourage - lastEncouragementTime > ENERGY_PHYSICS.ENCOURAGEMENT_COOLDOWN) {
      if (speed > 0.2) { flashFeedback('AMAZING! 🎉'); lastEncouragementTime = nowEncourage; }  // 20%+ speed
      else if (speed > 0.08) { flashFeedback('KEEP IT UP!'); lastEncouragementTime = nowEncourage; }  // 8-20% speed
      else if (speed > 0.03 && speed <= 0.08) { flashFeedback('SPEED UP!'); lastEncouragementTime = nowEncourage; } // 3-8% speed
    }

    // Parallax ring effects
    if (ring1) ring1.classList.toggle('active', energy > 0.1);
    if (ring2) ring2.classList.toggle('active', energy > 0.4);
    if (ring3) ring3.classList.toggle('active', energy > 0.7);

    // Spawn hearts when scrolling fast
    const nowHeart = performance.now();
    if (speed > 0.12 && nowHeart - lastHeartTime > heartInterval) {
      createHeart();
      lastHeartTime = nowHeart;
    }
  }

  function flashFeedback(msg) {
    const encouragement = $container.querySelector('#encouragement');
    if (encouragement) {
      encouragement.textContent = msg;
      encouragement.classList.add('level-up');
      setTimeout(() => encouragement.classList.remove('level-up'), 800);
    }
  }

  function applyVirtualScroll() {
    if (!bandsContainer || isTimedOut || playerIndex === null) {
      needsUpdate = false; // RESET even on early return to avoid UI hang!
      return;
    }
    needsUpdate = false;

    // wrap offset to 0..cycleHeight - use translate3d for hardware acceleration
    const offset = ((virtualScroll % cycleHeight) + cycleHeight) % cycleHeight;
    bandsContainer.style.transform = `translate3d(0, ${-offset}px, 0)`;

    const scrollNorm = ((virtualScroll / cycleHeight) % 1 + 1) % 1;
    const hue = scrollNorm * 360;
    const sat = 0.8;
    const light = 0.6;
    const color = `hsl(${hue}, ${sat * 100}%, ${light * 100}%)`;
    const [r, g, b] = hslToRgb(hue / 360, sat, light);

    // Calculate scroll speed
    const now = performance.now();
    const dt = (now - lastSpeedTime) / 1000;
    let speed = 0;

    if (dt > 0) {
      const dist = Math.abs(virtualScroll - lastVirtualScroll);
      speed = Math.min(1.0, dist / (cycleHeight * dt * ENERGY_PHYSICS.SPEED_CALIBRATION_FACTOR));
      lastVirtualScroll = virtualScroll;
      lastSpeedTime = now;
    }

    // Update contribution meter (replaces scrollNorm for energy feedback)
    playerContribution = Math.min(1.0, playerContribution + (speed * ENERGY_PHYSICS.CONTRIBUTION_GAIN));
    playerContribution *= ENERGY_PHYSICS.CONTRIBUTION_DECAY;
    const energy = playerContribution; // This is what the player sees!

    updateEnergyUI(energy, speed);
    audioEngine.updateScroll(scrollNorm, speed, muted || audioMuted, virtualScroll);

    // PERFORMANCE: Use mouse jitter as additive delta, while scroll determines global y-belt position
    // Range refinement: Use scrollNorm to sweep 0.2 to 0.8 of the map height
    const scrollYRange = 0.6; // Sweep 60% of total vertical space
    const targetY = (baseY - (scrollYRange / 2)) + (scrollNorm * scrollYRange);
    const clampedY = Math.min(1, Math.max(0, targetY));

    // PERFORMANCE: Throttle server updates (20Hz) AND use delta thresholding (0.5% change)
    const nowSend = performance.now();
    if (nowSend - lastSentTime > stateSendInterval) {
      const lastState = playerState.getValues();
      const dScroll = Math.abs(scrollNorm - (lastState.scroll || 0));
      const dEnergy = Math.abs(energy - (lastState.energy || 0));
      const dSpeed = Math.abs(speed - (lastState.speed || 0));
      const dY = Math.abs(clampedY - (lastState.y || 0));

      // Only send if significant change or initial sync
      if (dScroll > 0.005 || dEnergy > 0.01 || dSpeed > 0.05 || dY > 0.005 || !lastState.color) {
        lastSentTime = nowSend;

        // Guard: Only send updates if we are in the active player pool (0-24)
        if (playerIndex >= 25) return;

        playerState.set({
          color,
          scroll: scrollNorm,
          y: clampedY, // Combined scroll positioning
          energy,
          speed,
          hue,
          sat,
          light,
          r,
          g,
          b,
          lastPing: Date.now(), // Update ping on activity too
        });
      }
    }
  }

  function onWheel(e) {
    if (!scrollArea || isTimedOut || playerIndex === null) return;
    e.preventDefault();
    virtualScroll += e.deltaY;
    resetIdleClock();
    scheduleUpdate();
  }

  function onTouchStart(e) {
    if (!scrollArea || isTimedOut || playerIndex === null) return;
    resetIdleClock(); // Clear blur immediately on touch

    // Stop any existing momentum
    isDragging = true;
    velocity = 0;
    if (momentumID) cancelAnimationFrame(momentumID);

    if (e.touches.length > 0) {
      lastTouchY = e.touches[0].clientY;
    }
  }

  function onTouchMove(e) {
    if (!scrollArea || isTimedOut || playerIndex === null) return;
    if (e.touches.length > 0 && lastTouchY !== null) {
      const y = e.touches[0].clientY;
      const dy = lastTouchY - y;

      virtualScroll += dy;
      velocity = dy; // Capture velocity

      lastTouchY = y;
      resetIdleClock();
      scheduleUpdate();
      e.preventDefault();
    }
  }

  function onTouchEnd() {
    lastTouchY = null;
    isDragging = false;
    startMomentum();
  }

  function startMomentum() {
    if (Math.abs(velocity) < 0.1) return;

    function step() {
      if (isDragging || Math.abs(velocity) < 0.1) return;

      virtualScroll += velocity;
      velocity *= FRICTION; // Decay

      // Infinite loop wrap
      if (virtualScroll > cycleHeight * 100) virtualScroll -= cycleHeight * 100;

      scheduleUpdate();
      momentumID = requestAnimationFrame(step);
    }
    momentumID = requestAnimationFrame(step);
  }

  // ----------------------------------------------------------
  // 5. Position: baseX/baseY + offset from pointer
  // ----------------------------------------------------------
  function updateFromMouse(e) {
    if (!scrollArea || isTimedOut || playerIndex === null) return;

    resetIdleClock();

    const rect = scrollArea.getBoundingClientRect();
    const xNorm = (e.clientX - rect.left) / rect.width;
    const yNorm = (e.clientY - rect.top) / rect.height;

    const offsetRangeX = 0.25;
    const offsetRangeY = 0.25;

    const dx = (xNorm - 0.5) * 2 * offsetRangeX;
    const dy = (yNorm - 0.5) * 2 * offsetRangeY;

    let x = baseX + dx;
    let y = baseY + dy;

    x = Math.min(1, Math.max(0, x));
    y = Math.min(1, Math.max(0, y));

    playerState.set({ x, y });

    // PHASE 1: Update stereo panning immediately on mouse movement
    audioEngine.updatePan(x);
  }

  if (scrollArea) {
    scrollArea.addEventListener('wheel', onWheel, { passive: false });
    scrollArea.addEventListener('touchstart', onTouchStart, { passive: false });
    scrollArea.addEventListener('touchmove', onTouchMove, { passive: false });
    scrollArea.addEventListener('touchend', onTouchEnd);
    scrollArea.addEventListener('touchcancel', onTouchEnd);
    scrollArea.addEventListener('mousemove', updateFromMouse);
  }

  // ----------------------------------------------------------
  // 6. Click: send global trigger + playerId (for OSC + blink)
  // ----------------------------------------------------------
  $container.addEventListener('click', () => {
    if (isTimedOut || playerIndex === null) return;
    // console.log('[client] click -> global trigger + playerId');
    // global.set({ trigger: true, playerId: client.id });
  });

  // ----------------------------------------------------------
  // 7. Heartbeat system: ping server every 2 seconds
  // ----------------------------------------------------------
  setInterval(() => {
    if (isTimedOut || playerIndex === null) return;
    playerState.set({
      lastPing: Date.now()
    });
  }, 2000);
}

// launcher executes main()
launcher.execute(main, {
  numClients: parseInt(new URLSearchParams(window.location.search).get('emulate') || '') || 1,
});